import { PairingRedirectDefault } from '@easeltv/client-sdk';
import { RequestResult } from '@easeltv/remote';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useConfig } from '../../../hooks/useConfig/useConfig';
import TokenSection from '../../Sections/TokenSection';
import { H3, P } from '../../typography';
import { StyledPairingTemplate, SuccessIcon } from './PairingTemplate.styled';

/* ---------------------------------- Types --------------------------------- */

/**
 * PairingStep
 *
 * The different steps of the pairing process
 */
export enum PairingStep {
    EnterCode,
    Redirect,
    Success,
}

export interface DumbPairingTemplateI {
    /** The current step of the pairing process */
    step: PairingStep;

    /** The current value of the pairing code */
    code?: string;

    /** Callback to pair an already logged in user */
    pairLoggedInUser: () => Promise<void>;

    /** Flag for is a user is already logged in */
    isAuthenticated: boolean;

    /** Labels for this component */
    labels: {
        pairingCodeSuccessTitle: string;
        pairingCodeSuccessText: string;
    };

    validateToken: (token: string) => Promise<RequestResult<object | null>>;
}

/* ------------------------------- SignInStep ------------------------------- */

/**
 * SignInStep
 *
 * Small dumb component that will pair an already logged in user rather than show to auth options
 *
 * @param param0
 * @returns
 */
const RedirectStep = ({
    code,
    isAuthenticated,
    pairLoggedInUser,
}: Pick<
    DumbPairingTemplateI,
    'code' | 'pairLoggedInUser' | 'isAuthenticated'
>) => {
    const navigate = useNavigate();

    const [params] = useSearchParams();

    const action = params.get('action');

    const {
        registration: { default: defaultRedirect },
    } = useConfig();

    useEffect(() => {
        // If user is already logged in, we should complete the pairing
        if (isAuthenticated) {
            pairLoggedInUser();
        } else if (
            // We should redirect to signin if that action is defined in the query string or is the default with no
            // defined action
            action === 'login' ||
            (!action && defaultRedirect === PairingRedirectDefault.Login)
        ) {
            navigate('/signin', { state: { code } });
        } else if (
            // We should redirect to registration if that action is defined in the query string or is the default with
            // no defined action
            action === 'registration' ||
            (!action && defaultRedirect === PairingRedirectDefault.Registration)
        ) {
            navigate('/register', { state: { code } });
        } else {
            // Fallback should never be reached but rather be safer than sorry
            navigate('/register', { state: { code } });
        }
    }, [
        isAuthenticated,
        pairLoggedInUser,
        action,
        defaultRedirect,
        navigate,
        code,
    ]);

    return null;
};

/* -------------------------------- Component ------------------------------- */

/**
 * DumbPairingTemplate
 *
 * @param props DumbPairingTemplateI
 * @returns JSX.Element
 */
const DumbPairingTemplate = ({
    step,
    validateToken,
    code,
    pairLoggedInUser,
    isAuthenticated,
    labels,
}: DumbPairingTemplateI) => {
    const { pairingCodeSuccessText, pairingCodeSuccessTitle } = labels;
    switch (step) {
        case PairingStep.Success:
            return (
                <StyledPairingTemplate>
                    <H3>{pairingCodeSuccessTitle}</H3>
                    <P>{pairingCodeSuccessText}</P>
                    <SuccessIcon className="fa fa-registration-account-created-tv" />
                </StyledPairingTemplate>
            );
        case PairingStep.Redirect:
            return (
                <RedirectStep
                    code={code}
                    pairLoggedInUser={pairLoggedInUser}
                    isAuthenticated={isAuthenticated}
                />
            );

        case PairingStep.EnterCode:
        default:
            return (
                <StyledPairingTemplate>
                    <TokenSection
                        numberOfInputs={6}
                        validateToken={validateToken}
                    />
                </StyledPairingTemplate>
            );
    }
};

/* --------------------------------- Export --------------------------------- */

export default DumbPairingTemplate;
